/**
 * Settings Page
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Button, TextInput, Alert } from 'flowbite-react';
import { useStatus } from '../contexts/StatusContext';
import { userApi } from '../api/user';

const Settings: React.FC = () => {
  const { user, client, refreshUserData } = useStatus();
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'failure'; text: string } | null>(null);
  const edited = useRef(false);
  const profileEmail = useRef(user?.email);
  useEffect(() => {
    if (profileEmail.current !== user?.email) {
      edited.current = false;
      profileEmail.current = user?.email;
      setSaveMessage(null);
    }
    if (!edited.current) {
      setFirstName(user?.first_name || '');
      setLastName(user?.last_name || '');
    }
  }, [user?.email, user?.first_name, user?.last_name]);

  const handleSaveProfile = async () => {
    setIsSaving(true);
    setSaveMessage(null);
    try {
      const result = await userApi.updateProfile({
        first_name: firstName,
        last_name: lastName,
      });
      if (result.success) {
        setSaveMessage({ type: 'success', text: 'Profile updated successfully' });
        edited.current = false;
        await refreshUserData();
      } else {
        setSaveMessage({ type: 'failure', text: result.error || 'Failed to update profile' });
      }
    } catch {
      setSaveMessage({ type: 'failure', text: 'Network error. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your account and preferences
        </p>
      </div>

      {/* Profile Settings */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <TextInput
              type="email"
              value={user?.email || ''}
              disabled
              className="bg-gray-50"
            />
            <p className="mt-1 text-xs text-gray-500">
              Email cannot be changed
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <TextInput
                type="text"
                value={firstName}
                onChange={(e) => { edited.current = true; setFirstName(e.target.value); }}
                disabled={isSaving || !user}
                placeholder="First name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <TextInput
                type="text"
                value={lastName}
                onChange={(e) => { edited.current = true; setLastName(e.target.value); }}
                disabled={isSaving || !user}
                placeholder="Last name"
              />
            </div>
          </div>
          {saveMessage && (
            <Alert color={saveMessage.type === 'success' ? 'success' : 'failure'}>
              {saveMessage.text}
            </Alert>
          )}
          <div>
            <Button onClick={handleSaveProfile} disabled={isSaving || !user}>
              {isSaving ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Account Info */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center py-2 border-b">
            <div>
              <p className="text-sm font-medium text-gray-900">Subscription</p>
              <p className="text-sm text-gray-500">
                {(client?.subscription_tier || 'free').charAt(0).toUpperCase() + (client?.subscription_tier || 'free').slice(1)}
              </p>
            </div>
            <a
              href="/subscriptions"
              className="text-sm text-mindmeld-primary hover:text-blue-700"
            >
              Manage
            </a>
          </div>
          <div className="flex justify-between items-center py-2 border-b">
            <div>
              <p className="text-sm font-medium text-gray-900">Member Since</p>
              <p className="text-sm text-gray-500">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
          <div className="flex justify-between items-center py-2">
            <div>
              <p className="text-sm font-medium text-gray-900">Client ID</p>
              <p className="text-sm text-gray-500 font-mono">{user?.client_id || 'N/A'}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* API Keys */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">API Access</h3>
        <p className="text-sm text-gray-500 mb-4">
          Manage API keys for programmatic access
        </p>
        <Link to="/api-tokens" className="text-sm font-medium text-mindmeld-primary hover:underline">Manage API tokens</Link>
      </Card>
    </div>
  );
};

export default Settings;
